import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { HandCoins, TrendingUp, Wallet, ArrowDownCircle, PiggyBank } from 'lucide-react';
import { SyndicatorKPICards } from '../syndication/KPICards';
import { formatCurrency } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SyndicatorDashboard = () => {
  const { getAuthHeaders, user } = useAuth();
  const [summary, setSummary] = useState([]);
  const [mySyndicatorData, setMySyndicatorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all');

  const fetchSyndicatorData = useCallback(async () => {
    try {
      const params = dateRange !== 'all' ? { range: dateRange } : {};
      const response = await axios.get(`${API}/syndications/summary`, {
        headers: getAuthHeaders(),
        params,
      });
      
      const summaryData = response.data || [];
      setSummary(summaryData);
      
      // For non-admin syndicators, find their own data
      if (!user?.roles?.includes('admin') && user?.contact_id) {
        const myData = summaryData.find(s => s.syndicator_id === user.contact_id);
        setMySyndicatorData(myData || null);
      } else if (summaryData.length === 1) {
        // If only one syndicator returned, use that
        setMySyndicatorData(summaryData[0]);
      } else {
        // Calculate aggregate for admin view
        const aggregate = {
          syndicator_name: 'All Syndicators',
          total_invested: summaryData.reduce((sum, s) => sum + (s.total_invested || s.advance_amount || 0), 0),
          advance_amount: summaryData.reduce((sum, s) => sum + (s.advance_amount || 0), 0),
          target_collection: summaryData.reduce((sum, s) => sum + (s.target_collection || 0), 0),
          running_collection: summaryData.reduce((sum, s) => sum + (s.running_collection || 0), 0),
          total_collected: summaryData.reduce((sum, s) => sum + (s.total_collected || 0), 0),
          total_fees_collected: summaryData.reduce((sum, s) => sum + (s.total_fees_collected || 0), 0),
          withdrawals: summaryData.reduce((sum, s) => sum + (s.withdrawals || 0), 0),
          balance: summaryData.reduce((sum, s) => sum + (s.balance || 0), 0),
          deals_count: summaryData.reduce((sum, s) => sum + (s.deals_count || s.deals?.length || 0), 0),
        };
        setMySyndicatorData(aggregate);
      }
    } catch (error) {
      console.error('Failed to fetch syndicator data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user, dateRange]);

  useEffect(() => {
    fetchSyndicatorData();
  }, [fetchSyndicatorData]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If no syndicator data found
  if (!mySyndicatorData) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Syndicator Dashboard</h2>
          <p className="text-sm text-gray-500">Welcome back, {user?.name}</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <HandCoins className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No syndication data found for your account.</p>
            <p className="text-sm text-gray-400 mt-2">Contact an administrator if you believe this is an error.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Invested',
      value: formatCurrency(mySyndicatorData.total_advance || mySyndicatorData.advance_amount || 0),
      icon: PiggyBank,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Target Collection',
      value: formatCurrency(mySyndicatorData.total_target || mySyndicatorData.target_collection || 0),
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Running Collection',
      value: formatCurrency(mySyndicatorData.total_collected || mySyndicatorData.running_collection || 0),
      icon: HandCoins,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Withdrawals',
      value: formatCurrency(mySyndicatorData.total_withdrawals || mySyndicatorData.withdrawals || 0),
      icon: ArrowDownCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      title: 'Available Balance',
      value: formatCurrency(mySyndicatorData.total_balance || mySyndicatorData.balance || 0),
      icon: Wallet,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
  ];

  return (
    <div>
      {/* Header with filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Syndicator Dashboard</h2>
          <p className="text-sm text-gray-500">
            {mySyndicatorData.syndicator_name || user?.name} - {mySyndicatorData.deals?.length || mySyndicatorData.deals_count || 0} Active Deals
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex items-center space-x-2">
          <span className="text-sm text-gray-600">Period</span>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32 h-9">
              <SelectValue placeholder="Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="month">Last 30 days</SelectItem>
              <SelectItem value="week">Last 7 days</SelectItem>
              <SelectItem value="day">Today</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {stat.title}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a href="/syndicated-deals" className="p-4 border rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
              <HandCoins className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium">View Deals</p>
                <p className="text-sm text-gray-500">See all syndicated deals</p>
              </div>
            </a>
            <a href="/syndication-withdrawals" className="p-4 border rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
              <ArrowDownCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium">Withdrawals</p>
                <p className="text-sm text-gray-500">Request withdrawals</p>
              </div>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SyndicatorDashboard;
